import React, { useEffect, useState } from "react";
import axios from "axios";
import { Modal, Button } from "react-bootstrap";
import { isAddonCategory, mapCatalogProduct, normalizeSearch } from "./catalog";
import "./BreadAddonModal.css";

export default function BreadAddonModal({ item, orderItems, onClose, onSave, onEditNote, deliveryMode = false, apiBase, storeId }) {
  const [products, setProducts] = useState([]);
  const [selections, setSelections] = useState({});
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [error, setError] = useState("");
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!item) return undefined;
    let active = true;
    const initial = {};
    for (const addon of orderItems.filter((line) => String(line._addonParent) === String(item._uniqueId || item.id))) initial[addon.id] = addon.quantity;
    setSelections(initial);
    setSearch("");
    setProducts([]);
    setError("");
    setLoading(true);
    const loadProducts = async () => {
      try {
        let catalog;
        if (window.desktopPos) {
          const groups = await window.desktopPos.catalog();
          catalog = groups.filter((group) => isAddonCategory(group.category))
            .flatMap((group) => group.products.map((product) => mapCatalogProduct(product, group.category.MaNLSP, group.category.TenLoaiSP)));
        } else {
          if (!apiBase || !storeId) throw new Error("Không tìm thấy thông tin cửa hàng để lấy món ăn kèm.");
          const query = new URLSearchParams({ IDCH: storeId, MaNLSP: "NLBL" });
          const response = await axios.get(`${apiBase}/api/san-pham?${query}`, { timeout: 15000 });
          if (!Array.isArray(response.data)) throw new Error("Danh sách món ăn kèm không hợp lệ.");
          catalog = response.data.map((product) => mapCatalogProduct(product, "NLBL", "Nguyên liệu bán lẻ / ăn kèm"));
        }
        const result = [...new Map(catalog.map((product) => [String(product.id), {
          // Keep the store price so the cart can reprice when Grab is toggled.
          ...product, basePrice: product.price, price: deliveryMode ? product.price2 : product.price,
        }])).values()];
        if (active) setProducts(result);
      } catch (err) {
        if (active) setError(err.message || "Không tải được món ăn kèm. Vui lòng thử lại.");
      } finally {
        if (active) setLoading(false);
      }
    };
    loadProducts();
    return () => { active = false; };
    // Snapshot selections on opening; do not reset when cart changes elsewhere.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [item, deliveryMode, apiBase, storeId, retryKey]);

  const changeQuantity = (id, delta) => setSelections((current) => ({ ...current,
    [id]: Math.max(0, (Number(current[id]) || 0) + delta) }));
  const selected = products.filter((product) => Number(selections[product.id]) > 0);
  const total = selected.reduce((sum, product) => sum + product.price * selections[product.id], 0);
  const filteredProducts = products.filter((product) => normalizeSearch(`${product.id} ${product.name}`).includes(normalizeSearch(search)));

  return <Modal show={!!item} onHide={onClose} centered size="lg"
    dialogClassName="desktop-addon-modal order-bread-addon-modal" aria-labelledby="bread-addon-title"
    onKeyDown={(event) => event.stopPropagation()}>
    <Modal.Header closeButton>
      <div><span className="desktop-eyebrow">THÊM VÀO MÓN</span><Modal.Title id="bread-addon-title">Đồ ăn kèm</Modal.Title><p>{item?.name}</p></div>
    </Modal.Header>
    <Modal.Body>
      <input className="form-control" aria-label="Tìm đồ ăn kèm" placeholder="Tìm tên hoặc mã đồ ăn kèm…" value={search} onChange={(e) => setSearch(e.target.value)} />
      {loading ? <div className="desktop-loading-inline" role="status">Đang tải món ăn kèm…</div> : error ? <div className="alert alert-danger mt-3" role="alert">
        {error} <Button variant="outline-danger" size="sm" onClick={() => setRetryKey((key) => key + 1)}>Thử lại</Button>
      </div> : <>
        {products.length === 0 && <p className="text-muted mt-3">Chưa có món ăn kèm tại cửa hàng này.</p>}
        {products.length > 0 && filteredProducts.length === 0 && <p className="text-muted mt-3">Không tìm thấy món ăn kèm phù hợp.</p>}
        <div className="desktop-catalog-grid">
          {filteredProducts.map((product) => <div className={`desktop-addon-card ${selections[product.id] ? "selected" : ""}`} key={`${product.category}:${product.id}`}>
            <button type="button" className="bread-addon-card-select" aria-label={`Chọn ${product.name}`} onClick={() => changeQuantity(product.id, 1)}>
              <strong>{product.name}</strong><span>{product.price.toLocaleString("vi-VN")} ₫ / {product.unit || "phần"}</span>
            </button>
            <div className="desktop-stepper">
              <button type="button" aria-label={`Giảm ${product.name}`} disabled={!selections[product.id]} onClick={() => changeQuantity(product.id, -1)}>−</button>
              <output aria-label={`Số lượng ${product.name}`}>{selections[product.id] || 0}</output>
              <button type="button" aria-label={`Thêm ${product.name}`} onClick={() => changeQuantity(product.id, 1)}>+</button>
            </div>
          </div>)}
        </div>
      </>}
    </Modal.Body>
    <Modal.Footer>
      <Button variant="outline-secondary" onClick={onEditNote}>Ghi chú / số lượng món chính</Button>
      <span className="desktop-addon-total">Ăn kèm: <b>{total.toLocaleString("vi-VN")} ₫</b></span>
      <Button variant="outline-secondary" onClick={onClose}>Đóng</Button>
      <Button disabled={loading || !!error} onClick={() => onSave(selected.map((product) => ({ ...product, quantity: selections[product.id] })))}>Cập nhật món</Button>
    </Modal.Footer>
  </Modal>;
}
